import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { usePrefersDarkModeMediaQuery } from './useTheme';
import { getTheme } from './theme';
import { ViewportBreakpointsProvider } from './responsive/ViewportBreakpointsProvider';
import { ElementBreakpointsProvider } from './responsive/ElementBreakpointsProvider';
function isValidTheme(theme) {
    return theme === 'dark' || theme === 'light';
}
const CHRONO_THEME_PREFERENCE_KEY = 'CHRONOSPHERE_THEME';
const CHRONO_THEME_DEFAULT = 'light';
/**
 * Encapsulates the Chronosphere theme, passing through to the Material UI
 * theme provider. Will try to provide a default mode when one is not provided
 * by the consumer.
 */ export const ThemeProvider = ({ children, applicationRoot, mode, baselineCss = true, isNestedTheme })=>{
    // Get default value in ascending order of importance
    // 3. Default theme
    let defaultMode = CHRONO_THEME_DEFAULT;
    // 2. Media query - set at the OS level or in the user agent
    const userSpecifiedDarkMode = usePrefersDarkModeMediaQuery();
    defaultMode = userSpecifiedDarkMode ? 'dark' : defaultMode;
    // 1. Set in local storage - managed by ThemeProvider directly
    const val = window.localStorage.getItem(CHRONO_THEME_PREFERENCE_KEY);
    defaultMode = isValidTheme(val) ? val : defaultMode;
    // Use mode set by ThemeProvider consumer or default
    const themeMode = mode ?? defaultMode;
    // Always sync changes to local storage so we can use as default above
    window.localStorage.setItem(CHRONO_THEME_PREFERENCE_KEY, themeMode);
    const theme = useMemo(()=>getTheme(themeMode, applicationRoot), [
        themeMode,
        applicationRoot
    ]);
    return /*#__PURE__*/ _jsxs(MuiThemeProvider, {
        theme: theme,
        children: [
            baselineCss && /*#__PURE__*/ _jsx(CssBaseline, {}),
            isNestedTheme ? children : // The `ElementBreakpointsProvider` automatically provided is intended to be used for the main content on a
            // page (usually handled by the `ProtectedApp` component in `cloud`). Additional use cases (e.g. Drawers) should
            // configure a separate `ElementBreakpointsProvider` to avoid overriding the configuration for the main content
            // on the page.
            /*#__PURE__*/ _jsx(ViewportBreakpointsProvider, {
                children: /*#__PURE__*/ _jsx(ElementBreakpointsProvider, {
                    children: children
                })
            })
        ]
    });
};

//# sourceMappingURL=ThemeProvider.js.map